import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import {
  Box,
  Divider,
  Breadcrumbs,
  Link,
  Typography,
} from '@weave-mui/material';
import { Add as AddIcon } from '@mui/icons-material';

import PageTitle from 'components/PageTitle';
import Button, { buttonVariants } from '@weave-mui/button';
import WeaveMuiThemeProvider from 'components/WeaveMuiThemeProvider';
import WeaveConfirmationModal from 'components/WeaveConfirmationModal';
import LoadingSpinner from 'components/LoadingSpinner';

import EmptyGlobalFieldsState from './EmptyGlobalFieldsState';
import GlobalFieldsList from './GlobalFieldsList';
import GlobalFieldsModal from '../Hosting/GlobalFieldsModal';

import { getGlobalFields, postGlobalFields } from 'utils/api.service';
import { loadGlobalFields } from 'store/actions/hosting_actions';
import { addNewNotification } from 'store/actions/notification_actions';

/**
 * GlobalFields container component for managing global fields
 * This component is responsible for fetching and managing global fields data
 */
const GlobalFields = () => {
  const history = useHistory();
  const { category } = useParams();
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [fieldToEdit, setFieldToEdit] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [fieldToDelete, setFieldToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [loading, setLoading] = useState(true);

  const dispatch = useDispatch();
  const globalFields = useSelector((state) => state.hosting.globalFields);
  const menu = useSelector((state) => state.app.menu);

  // Extract org and workstream from menu state (same pattern as Hosting.js)
  const org = menu.service;
  const workstream = menu[org];
  const workstreamName = menu.categoryName;

  const fetchGlobalFields = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getGlobalFields(org, workstream);
      if (response.data) {
        dispatch(loadGlobalFields(response.data));
      }
    } catch (error) {
      dispatch(addNewNotification('Failed to load global fields', 'danger'));
    } finally {
      setLoading(false);
    }
  }, [org, workstream, dispatch]);

  useEffect(() => {
    if (org && workstream) {
      fetchGlobalFields();
    }
  }, [org, workstream, fetchGlobalFields]);

  const handleCreateGlobalField = () => {
    setModalMode('create');
    setFieldToEdit(null);
    setShowModal(true);
  };

  // Opens modal to edit a specific global field
  const handleEditField = (field) => {
    setModalMode('edit');
    setFieldToEdit(field);
    setShowModal(true);
  };

  const handleDeleteField = (field) => {
    setFieldToDelete(field);
    setShowDeleteModal(true);
  };

  const confirmDeleteField = async () => {
    if (!fieldToDelete) return;

    try {
      setIsDeleting(true);

      // Filter out the field to delete
      const updatedFields = {
        fields: globalFields.fields.filter((f) => f.key !== fieldToDelete.key),
      };

      const response = await postGlobalFields(org, workstream, updatedFields);

      if (response.data.status === 'success') {
        dispatch(loadGlobalFields(updatedFields));
        dispatch(
          addNewNotification('Global field deleted successfully', 'success')
        );
      } else {
        dispatch(
          addNewNotification(
            response.data.message || 'Failed to delete global field',
            'danger'
          )
        );
      }
    } catch (error) {
      dispatch(
        addNewNotification(
          error.response?.data?.detail || 'Failed to delete global field',
          'danger'
        )
      );
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
      setFieldToDelete(null);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setFieldToEdit(null);
    setModalMode('create');
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setFieldToDelete(null);
  };

  // Check if there are any global fields
  const hasGlobalFields =
    globalFields &&
    globalFields.fields &&
    globalFields.fields.length > 0 &&
    globalFields.fields.some((field) => field.key !== '');

  return (
    <WeaveMuiThemeProvider>
      <div className="global-fields-wrapper">
        {/* Breadcrumbs */}
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }} separator="/">
          <Link
            underline="hover"
            color="text.secondary"
            href="/"
            onClick={(e) => {
              e.preventDefault();
              history.push('/');
            }}
          >
            Home
          </Link>
          <Link
            underline="hover"
            color="text.secondary"
            href={`/hosting/${category}`}
            onClick={(e) => {
              e.preventDefault();
              history.push(`/hosting/${category}`);
            }}
          >
            Test hosting
          </Link>
          <Link
            underline="hover"
            color="text.secondary"
            href={`/hosting/${category}`}
            onClick={(e) => {
              e.preventDefault();
              history.push(`/hosting/${category}`);
            }}
          >
            {workstreamName}
          </Link>
          <Typography color="text.primary">Global Fields</Typography>
        </Breadcrumbs>

        <PageTitle
          title="Global Fields"
          description="Manage global fields to reuse data across your test cases"
        >
          {hasGlobalFields && (
            <Button
              onClick={handleCreateGlobalField}
              variant={buttonVariants.CONTAINED}
              startIcon={<AddIcon />}
              sx={{
                backgroundColor: '#000000 !important',
                color: '#ffffff !important',
                '&:hover': {
                  backgroundColor: '#333333 !important',
                },
                '&.MuiButton-contained': {
                  backgroundColor: '#000000 !important',
                  color: '#ffffff !important',
                },
                '&.MuiButton-contained:hover': {
                  backgroundColor: '#333333 !important',
                },
                fontSize: '14px',
                lineHeight: '18px',
                padding: '4px 12px',
                borderRadius: '4px',
                width: '186px',
                height: '32px',
                '& .MuiButton-startIcon': {
                  margin: '0 !important',
                },
              }}
            >
              Create Global Field
            </Button>
          )}
        </PageTitle>

        <Divider
          sx={{
            backgroundColor: 'rgba(0, 0, 0, 0.1)',
            height: '1px',
            border: 'none',
            my: 1,
            marginTop: '0px',
          }}
        />

        {loading ? (
          <Box sx={{ p: 3 }}>
            <LoadingSpinner text="Loading global fields..." />
          </Box>
        ) : hasGlobalFields ? (
          <GlobalFieldsList
            fields={globalFields.fields.filter((field) => field.key !== '')}
            onEdit={handleEditField}
            onDelete={handleDeleteField}
          />
        ) : (
          <EmptyGlobalFieldsState
            onCreateGlobalField={handleCreateGlobalField}
          />
        )}

        {showModal && (
          <GlobalFieldsModal
            isOpen={showModal}
            onClose={handleCloseModal}
            workstream={workstream}
            org={org}
            mode={modalMode}
            fieldToEdit={fieldToEdit}
          />
        )}

        <WeaveConfirmationModal
          open={showDeleteModal}
          title="Delete Global Field"
          message={`Are you sure you want to delete the global field "${fieldToDelete?.key}"? This action cannot be undone.`}
          confirmButtonText="Delete"
          onConfirm={confirmDeleteField}
          loading={isDeleting}
          loadingText="Deleting..."
          onClose={handleCloseDeleteModal}
        />
      </div>
    </WeaveMuiThemeProvider>
  );
};

export default GlobalFields;
